import { useCallback, useEffect, useRef, useState } from "react";
import type { HandbookLibraryResponse } from "@carbon-os/server/contract";
import { sessionFetch } from "./session";

/**
 * The Company handbook library: what Gatekeeper lets this person read
 * (carbon-gatekeeper#235). `unsupported` is a Gatekeeper from before the
 * library: the page says the handbook isn't available yet, never "nothing to
 * read". Only the Handbook page asks, so every other page load stays at two
 * requests.
 */
export type HandbookState =
  | { kind: "loading" }
  | { kind: "ready"; library: HandbookLibraryResponse }
  | { kind: "unsupported" }
  | { kind: "unavailable" };

export async function loadHandbook(fetchImpl: typeof fetch): Promise<Exclude<HandbookState, { kind: "loading" }>> {
  let res: Response;
  try {
    res = await fetchImpl("/api/handbook", { cache: "no-store", credentials: "same-origin", headers: { Accept: "application/json" } });
  } catch {
    return { kind: "unavailable" };
  }
  const body = (await res.json().catch(() => null)) as (HandbookLibraryResponse & { code?: string }) | null;
  if (res.status === 404 && body?.code === "handbook_unsupported") return { kind: "unsupported" };
  if (res.status !== 200 || !body || !Array.isArray(body.folders) || !Array.isArray(body.documents)) return { kind: "unavailable" };
  return { kind: "ready", library: { folders: body.folders, documents: body.documents, askAvailable: body.askAvailable === true } };
}

/** The Handbook page's answer; `refresh` re-reads quietly, keeping what is shown on failure. */
export function useHandbook(): { state: HandbookState; retry: () => void; refresh: () => void } {
  const [state, setState] = useState<HandbookState>({ kind: "loading" });
  const started = useRef(false);
  const load = useCallback(async (quiet: boolean) => {
    if (!quiet) setState({ kind: "loading" });
    const next = await loadHandbook((...args) => sessionFetch(...args));
    setState((current) => (quiet && next.kind !== "ready" ? current : next));
  }, []);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load(false);
  }, [load]);
  return { state, retry: () => void load(false), refresh: () => void load(true) };
}

/** A folder and the folders above it, from the top: what a breadcrumb links to. */
export function folderAncestors(folders: HandbookLibraryResponse["folders"], id: string | null): { id: string; name: string }[] {
  return ancestry(folders)(id);
}

/**
 * Looks up folders' ancestors with one map of the folders, so a list of
 * documents doesn't rebuild it for each one.
 */
export function ancestry(folders: HandbookLibraryResponse["folders"]): (id: string | null) => { id: string; name: string }[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  return (id) => {
    const path: { id: string; name: string }[] = [];
    let current = id ? byId.get(id) : undefined;
    while (current && path.length < 50) {
      path.unshift({ id: current.id, name: current.name });
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    return path;
  };
}

/** A folder's names from the top: ["Operations", "Leasing"]. */
export function folderPath(folders: HandbookLibraryResponse["folders"], id: string | null): string[] {
  return folderAncestors(folders, id).map((folder) => folder.name);
}

const ROMAN: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100 };

/** "IX" is 9; anything that isn't a well-formed numeral up to 399 is null. */
function romanValue(word: string): number | null {
  if (word === "" || !/^C{0,3}(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/.test(word)) return null;
  const values = [...word].map((letter) => ROMAN[letter] ?? 0);
  return values.reduce((total, value, index) => total + (value < (values[index + 1] ?? 0) ? -value : value), 0);
}

/**
 * The label that numbers a name in its list: "C" in "C. Layoff", "VII" in
 * "Section VII: Internet", "B" in "Appendix B". Capitals only, after at most
 * one leading word, and always closed by punctuation or the end of the name:
 * the "A" of "A Note on Benefits" and the "CV" of "Updated CV Guidelines" are
 * words, not labels.
 */
function listLabel(name: string): { label: string; index: number } | null {
  const match = /^(\p{L}+\s+)?([A-Z]{1,7})(?=[.:)]|$)/u.exec(name);
  return match?.[2] ? { label: match[2], index: match[1]?.length ?? 0 } : null;
}

/**
 * Names in reading order, numbers by value: "Step 2" before "Step 10". Whether
 * a label is a Roman numeral depends on its siblings, since C is a letter in
 * "A., B., C." and 100 nowhere: a list is numbered in Roman numerals when one
 * label is a numeral of two or more letters (II, IX) and no label is a letter
 * that can't be one (A, B, D). So "Section V" comes before "Section IX", and
 * "C. Layoff" and "Appendix C" stay letters.
 */
export function sortByName<T>(items: readonly T[], name: (item: T) => string): T[] {
  const labels = items.map((item) => listLabel(name(item)));
  const roman = labels.some((found) => found && found.label.length > 1 && romanValue(found.label) !== null)
    && !labels.some((found) => found && found.label.length === 1 && romanValue(found.label) === null);
  const keyed = items.map((item, index) => {
    const text = name(item), found = labels[index], value = roman && found ? romanValue(found.label) : null;
    const key = found && value !== null ? text.slice(0, found.index) + String(value) + text.slice(found.index + found.label.length) : text;
    return { item, text, key };
  });
  return keyed
    .sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true, sensitivity: "base" }) || a.text.localeCompare(b.text))
    .map(({ item }) => item);
}

/**
 * The folders depth-first, each with its depth, siblings in reading order. A
 * folder whose parent isn't in the list, or that sits in a loop of parents, is
 * shown at the top, never dropped.
 */
export function folderTree(folders: HandbookLibraryResponse["folders"]): { id: string; name: string; parentId: string | null; depth: number }[] {
  const known = new Set(folders.map((folder) => folder.id));
  const children = new Map<string | null, HandbookLibraryResponse["folders"]>();
  for (const folder of folders) {
    const parent = folder.parentId && known.has(folder.parentId) ? folder.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), folder]);
  }
  const out: { id: string; name: string; parentId: string | null; depth: number }[] = [];
  const seen = new Set<string>();
  const visit = (parentId: string | null, depth: number) => {
    for (const folder of sortByName(children.get(parentId) ?? [], (child) => child.name)) {
      if (seen.has(folder.id)) continue;
      seen.add(folder.id);
      out.push({ id: folder.id, name: folder.name, parentId: parentId, depth });
      visit(folder.id, depth + 1);
    }
  };
  visit(null, 0);
  // A loop of parents (A in B, B in A) never reaches the top: start each one there.
  for (const folder of sortByName(folders, (unvisited) => unvisited.name)) {
    if (seen.has(folder.id)) continue;
    seen.add(folder.id);
    out.push({ id: folder.id, name: folder.name, parentId: null, depth: 0 });
    visit(folder.id, 1);
  }
  return out;
}

/** How many documents sit in each folder, counting every folder below it. */
export function documentCounts(library: HandbookLibraryResponse): Map<string, number> {
  const ancestors = ancestry(library.folders);
  const counts = new Map<string, number>();
  for (const document of library.documents) {
    for (const folder of ancestors(document.folderId)) counts.set(folder.id, (counts.get(folder.id) ?? 0) + 1);
  }
  return counts;
}

/** Lower case without accents or punctuation: "401(k)" is "401k", "P.T.O." is "pto". */
function fold(text: string): string {
  return text.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^\p{L}\p{N}\s]+/gu, "");
}

/**
 * Documents matching every word of the query in their title or the names of
 * the folders they sit in, so "benefits 401k" finds the 401(k) page in
 * Section V: Employee Benefits. Punctuation is ignored and so are the spaces
 * between words, so "time-off" finds "time off". Title matches come first.
 */
export function searchDocuments(library: HandbookLibraryResponse, query: string): HandbookLibraryResponse["documents"] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const ancestors = ancestry(library.folders);
  const searchable = (text: string) => { const folded = fold(text); return `${folded} ${folded.replace(/\s+/g, "")}`; };
  const matches = library.documents.flatMap((document) => {
    const title = searchable(document.title);
    const place = searchable(ancestors(document.folderId).map((folder) => folder.name).join(" "));
    if (!words.every((word) => title.includes(word) || place.includes(word))) return [];
    return [{ document, inTitle: words.filter((word) => title.includes(word)).length }];
  });
  // Array sort is stable, so titles stay in reading order within each rank.
  return sortByName(matches, (match) => match.document.title)
    .sort((a, b) => b.inTitle - a.inTitle)
    .map((match) => match.document);
}

/**
 * A folder's own documents in reading order; `null` is the unfiled ones, which
 * includes any in a folder the list doesn't have. One titled like the folder
 * is its introduction ("Section I: Equal Employment Opportunity Statement" in
 * that folder) and comes first; the rest go by title, so "A." before "B.".
 */
export function folderDocuments(library: HandbookLibraryResponse, folderId: string | null): HandbookLibraryResponse["documents"] {
  const known = new Set(library.folders.map((folder) => folder.id));
  const name = folderId === null ? null : library.folders.find((folder) => folder.id === folderId)?.name.trim().toLowerCase();
  const here = library.documents.filter((document) => folderId === null
    ? document.folderId === null || !known.has(document.folderId)
    : document.folderId === folderId);
  const intro = here.filter((document) => document.title.trim().toLowerCase() === name);
  return [...intro, ...sortByName(here.filter((document) => !intro.includes(document)), (document) => document.title)];
}

/**
 * Every document in a folder and the folders below it, in the order a book
 * would have them: the folder's own documents, then each subfolder in turn,
 * all the way down.
 */
export function folderBook(library: HandbookLibraryResponse, folderId: string): HandbookLibraryResponse["documents"] {
  const tree = folderTree(library.folders);
  const start = tree.findIndex((folder) => folder.id === folderId);
  const top = tree[start];
  if (!top) return [];
  const chapters = [top];
  for (const folder of tree.slice(start + 1)) {
    if (folder.depth <= top.depth) break;
    chapters.push(folder);
  }
  return chapters.flatMap((chapter) => folderDocuments(library, chapter.id));
}
